import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import * as reportsApi from 'services/reportsApi';
import type { LedgerEntryRow, ReportSummary, ReportsDateRange } from 'types/reports';

// page/pageSize deliberately live only in `state.app.table` (StyledTable's shared pagination
// widget), never here — a second copy is exactly how the widget's "10 rows" footer and the
// backend's actual page size drifted apart before.
interface TransactionsState {
    items: LedgerEntryRow[];
    total: number;
    loading: boolean;
    error: string | null;
    inflightParishId: string | null;
    /** RTK's per-dispatch requestId — the source of truth for "is this response still current?" */
    latestRequestId: string | null;
}

interface SummaryState {
    data: ReportSummary | null;
    loading: boolean;
    error: string | null;
    inflightParishId: string | null;
    latestRequestId: string | null;
}

interface ReportsState {
    transactions: TransactionsState;
    summary: SummaryState;
}

const initialState: ReportsState = {
    transactions: {
        items: [],
        total: 0,
        loading: false,
        error: null,
        inflightParishId: null,
        latestRequestId: null,
    },
    summary: {
        data: null,
        loading: false,
        error: null,
        inflightParishId: null,
        latestRequestId: null,
    },
};

type FetchTransactionsArg = { parishId: string; page?: number; pageSize?: number } & ReportsDateRange;
type FetchSummaryArg = { parishId: string } & ReportsDateRange;

export const fetchReportTransactions = createAsyncThunkWrapper<
    Awaited<ReturnType<typeof reportsApi.fetchTransactions>> | null,
    FetchTransactionsArg
>(
    'reports/fetchTransactions',
    async ({ parishId, page, pageSize, from, to }, { getState }) => {
        if (!parishId) return null;
        try {
            const result = await reportsApi.fetchTransactions(parishId, { page, pageSize, from, to });
            if (parishSelectionMoved(getState, parishId)) return null;
            return result;
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load transactions' }
);

export const fetchReportsSummary = createAsyncThunkWrapper<ReportSummary | null, FetchSummaryArg>(
    'reports/fetchSummary',
    async ({ parishId, from, to }, { getState }) => {
        if (!parishId) return null;
        try {
            const summary = await reportsApi.fetchReportSummary(parishId, { from, to });
            if (parishSelectionMoved(getState, parishId)) return null;
            return summary;
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load report summary' }
);

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        clearReports(state) {
            state.transactions = initialState.transactions;
            state.summary = initialState.summary;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchReportTransactions.pending, (state, action) => {
            const nextParishId = action.meta.arg.parishId;
            state.transactions.loading = true;
            state.transactions.error = null;
            // Soft refresh (same parish, e.g. next page): keep prior rows to avoid flicker.
            // Parish switch: drop rows so another parish's data cannot paint.
            if (state.transactions.inflightParishId !== nextParishId) {
                state.transactions.items = [];
                state.transactions.total = 0;
            }
            state.transactions.inflightParishId = nextParishId;
            state.transactions.latestRequestId = action.meta.requestId;
        });
        builder.addCase(fetchReportTransactions.fulfilled, (state, action) => {
            // A newer request (next page, edited date range, parish switch) has since been dispatched —
            // this response is stale even though it shares the same parishId. Drop it.
            if (action.meta.requestId !== state.transactions.latestRequestId) return;
            state.transactions.loading = false;
            if (!action.payload) return;
            state.transactions.items = action.payload.items;
            state.transactions.total = action.payload.total;
        });
        builder.addCase(fetchReportTransactions.rejected, (state, action) => {
            if (action.meta.requestId !== state.transactions.latestRequestId) return;
            state.transactions.loading = false;
            state.transactions.error = (action.payload as string) || 'Failed to load transactions';
        });

        builder.addCase(fetchReportsSummary.pending, (state, action) => {
            const nextParishId = action.meta.arg.parishId;
            state.summary.loading = true;
            state.summary.error = null;
            if (state.summary.inflightParishId !== nextParishId) {
                state.summary.data = null;
            }
            state.summary.inflightParishId = nextParishId;
            state.summary.latestRequestId = action.meta.requestId;
        });
        builder.addCase(fetchReportsSummary.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.summary.latestRequestId) return;
            state.summary.loading = false;
            if (!action.payload) return;
            state.summary.data = action.payload;
        });
        builder.addCase(fetchReportsSummary.rejected, (state, action) => {
            if (action.meta.requestId !== state.summary.latestRequestId) return;
            state.summary.loading = false;
            state.summary.error = (action.payload as string) || 'Failed to load report summary';
        });
    },
});

export const { clearReports } = reportsSlice.actions;
export default reportsSlice.reducer;
